// WHAT: Main app component for reminders management
// WHY:  Provides interface for viewing and adding reminders
// DOMAIN: Productivity → Reminders
// DEPENDENCIES: useReminders hook, ReminderItem component
// RISKS:
//   - NOTE: Only supports 'once' recurrence type - limited functionality
//   - NOTE: No edit or delete functionality - incomplete CRUD
//   - NOTE: Form validation is minimal - may allow invalid data

import { type FC, useState } from "react";

import { useReminders } from "./hooks";
import { ReminderItem } from "./screen/ReminderItem";

/**
 * WHAT:
 *   Main app component that provides interface for viewing and adding reminders
 *   with basic form input.
 *
 * WHY:
 *   Enables users to manage their reminders in a simple interface.
 *
 * BEHAVIOR:
 *   - Displays list of all reminders
 *   - Provides input field for new reminder title
 *   - Allows adding new reminders (once recurrence only)
 *   - Allows marking reminders as done
 *
 * RISKS:
 *   - NOTE: Only supports 'once' recurrence type - limited functionality
 *   - NOTE: No edit or delete functionality - incomplete CRUD
 *   - NOTE: Form validation is minimal - may allow invalid data
 *
 * @returns JSX.Element
 *
 */
export const RemindersApp: FC = () => {
  const { addReminder, markDone, reminders } = useReminders();
  const [title, setTitle] = useState("");

  const handleAdd = async () => {
    if (!title) {
      return;
    }
    await addReminder({
      description: null,
      recurrence_type: "once",
      repeat_interval: null,
      repeat_weekday: null,
      start_date: new Date().toISOString().split("T")[0] ?? "",
      title,
    });
    setTitle("");
  };

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-2xl font-bold">Reminders</h1>
      <div className="flex gap-2">
        <input
          className="flex-1 rounded-md p-2 bg-slate-800 text-white"
          onChange={(e) => {
            setTitle(e.target.value);
          }}
          placeholder="New reminder"
          value={title}
        />
        <button
          className="btn-primary"
          onClick={() => {
            void handleAdd();
          }}
        >
          Add
        </button>
      </div>
      <ul className="space-y-2">
        {reminders.map((r) => (
          <ReminderItem
            key={r.id}
            onMarkDone={() => {
              void markDone(r.id);
            }}
            reminder={r}
          />
        ))}
      </ul>
    </div>
  );
};
